import { useCallback, useMemo, useState } from 'react'
import Select from 'react-select'
import type { Entry, EntryCollection } from 'contentful'
import { cubicBezier, motion } from 'framer-motion'

import Image from '@/components/ui/image'
import Link from '@/components/link-component'
import Container from '@/components/container'
import PageWrapper from '@/components/page-wrapper'
import ArticleListCard from '@/components/article-list-card'
import ArticleListHeader from '@/components/article-list-header'
import FilterHeader from '@/components/ui/filters-header'
import TagButton from '@/components/ui/tag-button'
import type { TypeEventsSkeleton } from '@/types/ctf'
import { cx, dateDiff, dateFormat, dateTimeFormat, slugify } from '@/utils/strings'
import useGetArticles from '@/utils/useGetArticles'
import type { TagObject } from '@/utils/tags'
import { usePage } from '@inertiajs/react'
import CtaLink, { buttonStyles } from '@/components/ui/cta-link'
import colors from '@/constants/colors'
import { useTranslations } from '@/utils/locale'

type EventListProps = {
  posts: EntryCollection<TypeEventsSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>
  tags: TagObject[]
  seo: any
  headerNav: any
  footerNav: any
  headerBanners: any
  newsletterForm: any
}

type TypeEvent = Entry<TypeEventsSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>

type AddedEventNode = Omit<TypeEvent['fields'], 'eventDate'> & {
  heading: string
  label: string
  eventDate?: Date
}

enum EventState {
  IN_PROGRESS = 'in-progress',
  UPCOMING = 'upcoming',
  PAST = 'past',
  FEATURED = 'featured',
}

export default function EventList({
  seo = { fields: {} },
  posts,
  tags,
  headerNav,
  footerNav,
  newsletterForm,
  headerBanners,
}: EventListProps) {
  const { t } = useTranslations()
  const EVENT_STATE = {
    [EventState.IN_PROGRESS]: {
      type: EventState.IN_PROGRESS,
      heading: t('In Progress Events'),
      label: t('In Progress'),
    },
    [EventState.UPCOMING]: {
      type: EventState.UPCOMING,
      heading: t('Upcoming Events'),
      label: t('Live'),
    },
    [EventState.PAST]: {
      type: EventState.PAST,
      heading: t('Past Events'),
      label: t('Replay'),
    },
    [EventState.FEATURED]: {
      type: EventState.FEATURED,
      heading: t('Featured Events'),
      label: t('Featured'),
    },
  }

  const eventNodes = posts.items

  const page = usePage()
  const { numVisible, setNumVisible, numPerPage, selectedTag, setSelectedTag, didMountState } = useGetArticles(page.url)
  const [reactSelectedTag, setReactSelectedTag] = useState<{
    value: string
    label: string
    count: number
  } | null>(null)

  const getEventState = useCallback((event: TypeEvent) => {
    const oneHourInMilliseconds = 1000 * 60 * 60
    const eventTimestamp = Number(new Date(event.fields.eventDate || 0))
    const eventEndTimestamp = eventTimestamp + oneHourInMilliseconds
    const nowTimestamp = Number(new Date())

    if (eventTimestamp < nowTimestamp && eventEndTimestamp > nowTimestamp) {
      return EVENT_STATE[EventState.IN_PROGRESS]
    } else if (eventEndTimestamp > nowTimestamp) {
      return EVENT_STATE[EventState.UPCOMING]
    } else {
      return EVENT_STATE[EventState.PAST]
    }
  }, [])

  const demos = useMemo(
    () =>
      eventNodes
        .filter((event) => event.fields.tags?.includes('Demo'))
        .sort(
          (a, b) =>
            Number(new Date(b.fields.eventDate as unknown as string)) -
            Number(new Date(a.fields.eventDate as unknown as string))
        )
        .reduce(
          (acc, next) => {
            const tags = next.fields.tags || []
            const isFeatured = next.fields.featuredEvent
            if (isFeatured) {
              if (!acc.featuredProductDemo && tags.includes('Product')) {
                const state = getEventState(next)
                acc.featuredProductDemo = {
                  state,
                  event: next,
                }
              }
              if (!acc.featuredMspDemo && tags.includes('MSP')) {
                const state = getEventState(next)
                acc.featuredMspDemo = {
                  state,
                  event: next,
                }
              }
              if (!acc.featuredEnterpriseDemo && tags.includes('Enterprise')) {
                const state = getEventState(next)
                acc.featuredEnterpriseDemo = {
                  state,
                  event: next,
                }
              }
            } else {
              if (!acc.productDemo && tags.includes('Product')) {
                const state = getEventState(next)
                acc.productDemo = {
                  state,
                  event: next,
                }
              }
              if (!acc.mspDemo && tags.includes('MSP')) {
                const state = getEventState(next)
                acc.mspDemo = {
                  state,
                  event: next,
                }
              }
              if (!acc.enterpriseDemo && tags.includes('Enterprise')) {
                const state = getEventState(next)
                acc.enterpriseDemo = {
                  state,
                  event: next,
                }
              }
            }
            return acc
          },
          {
            productDemo: null,
            featuredProductDemo: null,
            mspDemo: null,
            featuredMspDemo: null,
            enterpriseDemo: null,
            featuredEnterpriseDemo: null,
          } as Record<
            string,
            {
              state: ReturnType<typeof getEventState>
              event: TypeEvent
            } | null
          >
        ),
    [eventNodes]
  )
  const productDemo = demos.featuredProductDemo || demos.productDemo
  const mspDemo = demos.featuredMspDemo || demos.mspDemo
  const enterpriseDemo = demos.featuredEnterpriseDemo || demos.enterpriseDemo

  let featuredEvents = useMemo(
    () =>
      eventNodes
        .filter((event) => event.fields.featuredEvent)
        .sort(
          (a, b) =>
            Number(new Date(a.fields.eventDate as unknown as string)) -
            Number(new Date(b.fields.eventDate as unknown as string))
        ),
    [eventNodes]
  )

  // Filter out the product demo events from the featured events
  // This is to avoid showing the product demo events twice
  const productDemoIds = [productDemo?.event.sys.id, mspDemo?.event.sys.id, enterpriseDemo?.event.sys.id]
  featuredEvents = featuredEvents.filter((event) => {
    if (productDemoIds.includes(event.sys.id)) {
      return false
    }
    return true
  })

  const eventNodesOrdered = useMemo(() => {
    const futureEventNodes: AddedEventNode[] = []
    const inProgressEventNodes: AddedEventNode[] = []
    const pastEventNodes: AddedEventNode[] = []
    const createEventNode = ({
      heading,
      label,
      eventNode,
    }: {
      heading: string
      label: string
      eventNode: TypeEvent
    }): AddedEventNode => ({
      ...eventNode.fields,
      heading,
      label,
      eventDate: new Date(eventNode.fields.eventDate?.toString()),
    })

    eventNodes.forEach((eventNode) => {
      const state = getEventState(eventNode)

      if (state.type === EventState.IN_PROGRESS) {
        inProgressEventNodes.push(
          createEventNode({
            ...state,
            eventNode,
          })
        )
      } else if (state.type === EventState.UPCOMING) {
        futureEventNodes.push(
          createEventNode({
            ...state,
            eventNode,
          })
        )
      } else {
        pastEventNodes.push(
          createEventNode({
            ...state,
            eventNode,
          })
        )
      }
    })

    futureEventNodes.sort((a, b) => {
      if ((a.eventDate || 0) < (b.eventDate || 0)) return -1
      return 1
    })

    const featuredNodes = !selectedTag
      ? featuredEvents.map((eventNode) => {
          return createEventNode({
            ...EVENT_STATE[EventState.FEATURED],
            eventNode,
          })
        })
      : []

    return [...featuredNodes, ...inProgressEventNodes, ...futureEventNodes, ...pastEventNodes]
  }, [eventNodes, featuredEvents, selectedTag])

  const events = useMemo(
    () =>
      eventNodesOrdered
        .filter(
          (event) =>
            !selectedTag ||
            selectedTag === 'all' ||
            event.tags?.map((tag) => tag.toLowerCase())?.includes(selectedTag.replaceAll('-', ' '))
        )
        .map((event) => ({
          ...event,
          eventDate: event?.eventDate ? new Date(event?.eventDate.toString()) : null,
        })),
    [eventNodesOrdered, selectedTag]
  )

  const renderEventDate = (event: (typeof events)[0]) => {
    return (
      event.eventDate && (
        <div className="text-secondary-500 text-xs font-medium">
          {event.heading === EVENT_STATE[EventState.PAST].heading
            ? dateFormat(event.eventDate.toString(), 'en-US')
            : dateTimeFormat(event.eventDate.toString(), 'en-US')}
          &nbsp;({dateDiff(event.eventDate.toString(), 'en-US')})
        </div>
      )
    )
  }

  const renderTags = (event: (typeof events)[0]) => (
    <div className="text-secondary-500 text-xs font-medium ">{(event.tags as unknown as string[]).join(', ')}</div>
  )

  const selectTags =
    Object.values(tags || {})
      .map((tag) => ({ value: slugify(tag.name), label: tag.name, count: tag.count }))
      .sort((a, b) => a.label.localeCompare(b.label)) || []
  selectTags.unshift({ label: 'All', value: '', count: eventNodes.length })

  return (
    <PageWrapper
      headerNav={headerNav}
      footerNav={footerNav}
      headerBanners={headerBanners}
      newsletterForm={newsletterForm}
      seo={seo}
    >
      <ArticleListHeader heading={t('Bitwarden Events')}>
        {selectedTag && (
          <h2 className="hidden h4 md:block lg:px-4 capitalize">
            {t('Tag')} &ldquo;{selectedTag.replaceAll('-', ' ')}&rdquo;
          </h2>
        )}
        <div className={`${!selectedTag && 'mb-6'} md:hidden`}>
          <FilterHeader title={t('Tags')} icon="&#xe95e;" />
          <Select
            name="tags"
            value={reactSelectedTag}
            options={selectTags}
            onChange={(selected) => {
              setNumVisible(numPerPage)
              if (selected) {
                setSelectedTag(selected?.value)
                setReactSelectedTag(selected)
              }
            }}
            isClearable={false}
            className="dark:text-gray-950"
          />
        </div>
        {!selectedTag && (productDemo || mspDemo || enterpriseDemo) && (
          <div>
            <h2 className="h4 lg:px-4 mb-6">{t('Product Demos')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-6 place-items-stretch [&_article]:h-full">
              {productDemo && (
                <ArticleListCard
                  image={productDemo.event.fields.seo?.fields?.ogImage || null}
                  title={t('Bitwarden Product Deep Dive')}
                  url={`/events/${productDemo.event.fields.slug}`}
                  cta={
                    productDemo.state.type === EventState.PAST
                      ? t('Watch the latest live demo')
                      : t('Join the live demo')
                  }
                />
              )}
              {mspDemo && (
                <ArticleListCard
                  image={mspDemo.event.fields.seo?.fields?.ogImage || null}
                  title={mspDemo.event.fields.seo?.fields.title || ''}
                  url={`/events/${mspDemo.event.fields.slug}`}
                  cta={
                    mspDemo.state.type === EventState.PAST
                      ? t('Watch the latest MSP demo')
                      : t('Join the next MSP demo')
                  }
                />
              )}
              {enterpriseDemo && (
                <ArticleListCard
                  image={enterpriseDemo.event.fields.seo?.fields?.ogImage || null}
                  title={enterpriseDemo.event.fields.seo?.fields.title || ''}
                  url={`/events/${enterpriseDemo.event.fields.slug}`}
                  cta={
                    enterpriseDemo.state.type === EventState.PAST
                      ? t('Watch the latest Enterprise demo')
                      : t('Join the next Enterprise demo')
                  }
                />
              )}
            </div>
          </div>
        )}
      </ArticleListHeader>

      <Container className="py-10 lg:px-10">
        <section className="md:flex pb-20">
          <div className="md:flex-1 md:pr-8">
            <div className={cx('lg:flex lg:flex-col lg:gap-12 lg:col-span-4', !didMountState && 'invisible')}>
              {events.slice(0, numVisible).map((event, index, elements) => {
                const prevEvent = elements[index - 1]
                return (
                  <div key={`${event.heading}-${event.internalName}`}>
                    {prevEvent?.heading !== event.heading && <h2 className="mb-6 h4">{event.heading}</h2>}
                    <motion.div
                      className="flex flex-col gap-8 mb-8 lg:mb-0"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{
                        duration: 0.15,
                        ease: cubicBezier(0, 0, 0.2, 1),
                      }}
                    >
                      <Link to={`/events/${event.slug}/`} key={`${event.slug}${selectedTag}`}>
                        <article
                          className={cx(
                            'text-center flex flex-col p-8 rounded-4xl bg-gray-100 transition-all duration-[250ms] hover:scale-[1.02] hover:bg-white hover:shadow-even-around lg:text-left dark:bg-gray-800 dark:hover:bg-gray-800'
                          )}
                        >
                          <div className="flex flex-col lg:flex-row gap-8">
                            <div className="lg:max-w-1/3 xl:max-w-1/2 mx-auto flex-shrink-0 [&_figure]:mb-0 xl:[&_figure]:mb-6 [&_figure]:mt-0">
                              {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                              {/* @ts-ignore */}
                              {event?.seo?.fields?.ogImage && (
                                <Image image={event?.seo?.fields?.ogImage} className="rounded-xl object-center" />
                              )}
                            </div>
                            <div>
                              {event?.seo?.fields?.title && (
                                <h3 className="text-2xl">{event.seo.fields.title.toString()}</h3>
                              )}
                              <div
                                className={cx(
                                  'inline-block px-1 mb-3 text-xs font-medium tracking-wider uppercase border-2 rounded border-primaryBlue',
                                  event.label === EVENT_STATE[EventState.UPCOMING].label &&
                                    'bg-gray-100 text-primaryBlue',
                                  event.label === EVENT_STATE[EventState.IN_PROGRESS].label &&
                                    'text-white border-transparent bg-accent-melon',
                                  event.label === EVENT_STATE[EventState.PAST].label && 'text-white bg-primaryBlue'
                                )}
                              >
                                {event.label}
                              </div>
                              <p>{event.seo?.fields?.description?.toString()}</p>
                              {event?.eventDate && <div className="block xl:hidden">{renderEventDate(event)}</div>}
                              <div className="pt-5 lg:hidden">{event.tags && renderTags(event)}</div>
                            </div>
                          </div>
                          <div className="hidden xl:flex xl:justify-between xl:gap-3 flex-shrink-0 xl:w-full xl:pl-3">
                            {event?.eventDate && renderEventDate(event)}
                            {event.tags && renderTags(event)}
                          </div>
                        </article>
                      </Link>
                    </motion.div>
                  </div>
                )
              })}

              <div>
                {numVisible < events.length && (
                  <div className="text-center mt-2">
                    <CtaLink
                      onClick={() => setNumVisible(numVisible + numPerPage)}
                      asButton
                      style={buttonStyles.btnSolid}
                      backgroundColor={colors.white}
                    >
                      Load More
                    </CtaLink>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="hidden md:block mx-auto w-[300px] mt-10 xl:mt-0">
            <FilterHeader title="Tags" icon="&#xe95e;" />

            <div className="flex flex-col gap-1">
              {selectTags.map((tag) => (
                <TagButton
                  key={tag.value}
                  selectedTag={selectedTag}
                  tag={tag}
                  handleOnClick={() => {
                    setNumVisible(numPerPage)
                    setSelectedTag(slugify(tag.value))
                    setReactSelectedTag(tag)
                  }}
                  count={tag.count}
                />
              ))}
            </div>
          </div>
        </section>
      </Container>
    </PageWrapper>
  )
}
